import {
  useCreateCustomLevelMutation,
  useUpdateCustomLevelMutation,
} from '@/app/redux/endpoints/customLevels';
import { useUpdateStudentMutation } from '@/app/redux/endpoints/students';
import { useAppDispatch, useAppSelector } from '@/app/redux/hooks';
import { useCustomLevels } from '@/app/redux/hooks/customLevels';
import { useCurrentOrganisationId } from '@/app/redux/hooks/organisation';
import { useStudents } from '@/app/redux/hooks/students';
import { setAttemptedProFeature } from '@/app/redux/slices/modalsSlice';
import { selectUserId } from '@/app/redux/slices/sessionSlice';
import {
  CreateCustomLevelInput,
  CustomLevel,
  CustomLevelStudentProgress,
  UpdateCustomLevelInput,
} from '@/common/types/customLevel';
import { DashboardViewContainer } from '@/common/ui/DashboardViewStyles';
import EditorHeader from '@/common/ui/EditorHeader';
import { UNKNOWN_ERROR_MESSAGE } from '@/common/ui/UnknownErrorMessage';
import { NotFoundError } from '@/common/utils/error';
import { GAMES } from '@/common/utils/helpers';
import trackMixpanelEvent from '@/common/utils/mixpanel';
import { getLevel } from '@/common/utils/scopeAndSequence';
import { useAutosave } from '@/common/utils/useAutosave';
import { Skeleton } from '@shadcn/components/skeleton';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import CustomLevelEditor, {
  BLANK_GAME_DATA,
  BLANK_LEVEL,
  GameId,
} from './CustomLevelEditor';
import CustomLevelEditorTour from './CustomLevelEditorTour';

const BLANK_META = {
  id: '',
  createdBy: '',
  organisationId: '',
  createdAt: 0,
  updatedAt: 0,
};

export default function CustomLevelEditorView() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const isNew = id === 'new';
  const navigate = useNavigate();

  const initialLevel = searchParams.get('level') ?? undefined;
  const initialStudentId = searchParams.get('studentId') ?? undefined;

  const { customLevels, isLoading, isFetching } = useCustomLevels();
  const { currentOrganisationId } = useCurrentOrganisationId();
  const userId = useAppSelector(selectUserId);
  const { students } = useStudents();

  const [updateCustomLevel] = useUpdateCustomLevelMutation();
  const [createCustomLevel, { isLoading: isCreating }] =
    useCreateCustomLevelMutation();
  const [updateStudent] = useUpdateStudentMutation();

  const serverLevel = isNew ? undefined : customLevels.find((l) => l.id === id);

  const [level, setLevel] = useState<CustomLevel>(() => {
    if (!isNew || !initialLevel) return { ...BLANK_LEVEL, ...BLANK_META };

    const levelNumber = parseInt(initialLevel);
    const levelData = getLevel(levelNumber);
    if (!levelData) return { ...BLANK_LEVEL, ...BLANK_META };

    const enabledGames = new Set(levelData.games);
    const game = <T,>(name: string, items: T[]) =>
      enabledGames.has(name)
        ? { enabled: true, items: [...items] }
        : BLANK_GAME_DATA;

    const games = {
      mysticMatch: game('mysticMatch', levelData.newGraphemes),
      wordWeaver: game('wordWeaver', levelData.wordSet),
      phonemeForge: game('phonemeForge', levelData.wordChain),
      sentenceBuilder: game('sentenceBuilder', levelData.sentences),
      wordSums: game('wordSums', levelData.wordSet),
      morphMaster: game('morphMaster', levelData.morphemeChain),
    };

    const initialStudents: Record<string, CustomLevelStudentProgress> = {};
    if (initialStudentId) {
      const gamesProgress: CustomLevelStudentProgress['gamesProgress'] = {};
      for (const gameName of GAMES) {
        if (enabledGames.has(gameName)) {
          gamesProgress[gameName] = {
            timesPlayed: 0,
            timesMastered: 0,
            currentHighestMastery: 0,
            completedAtNodeIndex: -1,
          };
        }
      }
      initialStudents[initialStudentId] = {
        completedPlaythroughs: 0,
        gamesProgress,
      };
    }

    return {
      ...BLANK_LEVEL,
      ...BLANK_META,
      description: `Copy of Level ${levelData.level} - ${levelData.description}`,
      students: initialStudents,
      ...games,
    };
  });

  const [isReady, setIsReady] = useState(isNew);
  const [lastSaved, setLastSaved] = useState<number | undefined>(undefined);

  const { isSaving } = useAutosave({
    saveFn: handleSave,
    trigger: [level],
    disabled: !isReady,
  });

  // Reset when navigating to a different level
  useEffect(() => {
    if (!isNew) setIsReady(false);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isNew && serverLevel && !isReady) {
      setLevel(serverLevel);
      setLastSaved(serverLevel.updatedAt);
      setIsReady(true);
    }
  }, [isNew, serverLevel, isReady]);

  function handleChange(updates: UpdateCustomLevelInput) {
    setLevel((prev) => ({ ...prev, ...updates }));
  }

  async function handleSave() {
    if (!userId || !currentOrganisationId) return;
    if (isNew) {
      try {
        const {
          id: _id,
          createdBy: _cb,
          organisationId: _oi,
          createdAt: _ca,
          updatedAt: _ua,
          ...rest
        } = level;
        const input: CreateCustomLevelInput = {
          ...rest,
          createdBy: userId,
          organisationId: currentOrganisationId,
        };
        const result = await createCustomLevel(input).unwrap();
        const pendingStudentIds = Object.keys(level.students);
        if (pendingStudentIds.length) {
          await Promise.all(
            pendingStudentIds.map((studentId) =>
              updateStudent({
                orgId: currentOrganisationId,
                studentId,
                updates: { customLevelId: result.id },
              }).unwrap(),
            ),
          );
        }
        trackMixpanelEvent('Custom Level Created', {
          customLevelId: result.id,
          enabledGames: GAMES.filter((g) => level[g].enabled),
          assignedStudentCount: pendingStudentIds.length,
        });
        setLastSaved(Date.now());
        navigate(`/dashboard/customlevels/${result.id}`, { replace: true });
      } catch {
        toast.error('Failed to save custom level', {
          description: UNKNOWN_ERROR_MESSAGE,
        });
      }
    } else {
      try {
        const {
          id: _id,
          createdBy: _cb,
          organisationId: _oi,
          createdAt: _ca,
          updatedAt: _ua,
          ...updates
        } = level;
        await updateCustomLevel({
          orgId: currentOrganisationId,
          id: id!,
          updates,
        }).unwrap();
        setLastSaved(Date.now());
      } catch {
        toast.error('Failed to save');
      }
    }
  }

  const assignedStudentIds = isNew
    ? Object.keys(level.students)
    : students.filter((s) => s.customLevelId === level.id).map((s) => s.id);

  async function handleSelectStudent(studentId: string) {
    if (!currentOrganisationId) return;
    const student = students.find((s) => s.id === studentId);
    if (!student) return;

    if (isNew) {
      const defaultGamesProgress: CustomLevelStudentProgress['gamesProgress'] =
        {};
      for (const game of GAMES) {
        if (level[game].enabled) {
          defaultGamesProgress[game] = {
            timesPlayed: 0,
            timesMastered: 0,
            currentHighestMastery: 0,
            completedAtNodeIndex: -1,
          };
        }
      }
      const currentStudents = level.students;
      const isRemoving = studentId in currentStudents;
      trackMixpanelEvent('Custom Level Learner Toggled', {
        studentId,
        assigned: !isRemoving,
      });
      if (isRemoving) {
        const { [studentId]: _, ...rest } = currentStudents;
        handleChange({ students: rest });
      } else {
        handleChange({
          students: {
            ...currentStudents,
            [studentId]: {
              completedPlaythroughs: 0,
              gamesProgress: defaultGamesProgress,
            },
          },
        });
      }
    } else {
      if (!level.id) return;
      const isAssigned = student.customLevelId === level.id;
      trackMixpanelEvent('Custom Level Learner Toggled', {
        customLevelId: level.id,
        studentId,
        assigned: !isAssigned,
      });
      const newCustomLevelId = isAssigned ? null : level.id;
      const currentStudents = level.students ?? {};
      let updatedStudents: CustomLevel['students'];
      if (isAssigned) {
        updatedStudents = { ...currentStudents };
        delete updatedStudents[studentId];
      } else {
        updatedStudents = {
          ...currentStudents,
          [studentId]: { completedPlaythroughs: 0, gamesProgress: {} },
        };
      }
      try {
        await Promise.all([
          updateStudent({
            orgId: currentOrganisationId,
            studentId,
            updates: { customLevelId: newCustomLevelId },
          }).unwrap(),
          handleChange({ students: updatedStudents }),
        ]);
      } catch {
        toast.error('Failed to update learner assignment');
      }
    }
  }

  function handleToggleGame(game: GameId, enabled: boolean) {
    if (isNew) {
      trackMixpanelEvent('Custom Level Game Toggled', { game, enabled });
      const updatedStudents = { ...level.students };
      for (const studentId of Object.keys(updatedStudents)) {
        const progress = { ...updatedStudents[studentId] };
        const gamesProgress = { ...progress.gamesProgress };
        if (enabled) {
          gamesProgress[game] ??= {
            timesPlayed: 0,
            timesMastered: 0,
            currentHighestMastery: 0,
            completedAtNodeIndex: -1,
          };
        } else {
          delete gamesProgress[game];
        }
        updatedStudents[studentId] = { ...progress, gamesProgress };
      }
      handleChange({
        [game]: { ...level[game], enabled },
        students: updatedStudents,
      });
    } else {
      trackMixpanelEvent('Custom Level Game Toggled', {
        customLevelId: level.id,
        game,
        enabled,
      });
      handleChange({ [game]: { ...level[game], enabled } });
    }
  }

  if (!isNew && (isLoading || isFetching)) {
    return (
      <DashboardViewContainer>
        <div className="p-4 space-y-4">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-lg" />
          ))}
        </div>
      </DashboardViewContainer>
    );
  }

  if (!isNew && !isLoading && !isFetching && !serverLevel) {
    throw new NotFoundError('Custom level not found');
  }

  return (
    <DashboardViewContainer>
      <EditorHeader
        name={level.description || 'Untitled Level'}
        onNameChange={(value) => handleChange({ description: value })}
        isSaving={isNew ? isCreating : isSaving}
        lastSaved={lastSaved}
        backPath="/dashboard/customlevels"
        actions={<CustomLevelEditorTour />}
      />
      <CustomLevelEditor
        level={level}
        onChange={handleChange}
        assignedStudentIds={assignedStudentIds}
        onSelectStudent={handleSelectStudent}
        onToggleGame={handleToggleGame}
      />
    </DashboardViewContainer>
  );
}

export function CustomLevelsDisabledRedirect() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    dispatch(setAttemptedProFeature('Custom Levels'));
    navigate('/dashboard/customlevels');
  }, [dispatch, navigate]);

  return null;
}
